/**
 * FINN leavers of the last three months, newest first and grouped by month, with each letter's ONE
 * status (`letterStatus`, the same words as the draft header). The proxy decides who is a leaver
 * and what "submitted" means (proxy-server/lib/referenceLetterLeavers.js); this only shows it. Every
 * status is a filter chip (`StatusFilter`): a chip's count and the rows it shows share one status.
 *
 * The row opens the letter (David, Oct 15 2026: buttons only where they DO something — Draft,
 * Remind). The name is the row's keyboard-reachable control, as on the interim list.
 */
import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { Banner } from '@finn-auto/design-system/atoms/banner';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Switch } from '@finn-auto/design-system/atoms/switch';
import { Loader2 } from 'lucide-react';
import { fetchReferenceLetterLeavers, markLetterDone } from '../../services/referenceLetterApi';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { ReferenceLetterLeaver, ReferenceLetterLeavers } from '../../types/referenceLetterDraft';
import { formatDate, markedDoneText } from './format';
import { DoneMenu } from './DoneMenu';
import { groupByMonth, isComplete, isReady } from './leaverGroups';
import { letterStatus, showsStatus, type StatusSelection } from './letterStatus';
import { StatusFilter } from './StatusFilter';
import { LetterStatusBadge } from './LetterStatusBadge';
import { ACTION_BUTTON, RemindControl } from './RemindControl';

const COLUMNS = 5;

/** A leaver's one status; marked Done by hand wins. */
const statusOf = (l: ReferenceLetterLeaver) => letterStatus({ ...(l.request ?? {}), done: Boolean(l.done) });


/**
 * The leaver table with its chips, future switch and search; a row hands the leaver to the page.
 * A new `refreshKey` reloads it (a letter was sent for signature or cancelled meanwhile).
 */
export function LeaverList({ onSelect, refreshKey = 0 }: { onSelect: (leaver: ReferenceLetterLeaver) => void; refreshKey?: number }) {
  const [future, setFuture] = useState(false);
  const [data, setData] = useState<ReferenceLetterLeavers | null>(null);
  // Kept with the switch position it belongs to, so "loading" and a stale error are derived, not set.
  const [failure, setFailure] = useState<{ future: boolean; message: string } | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusSelection>(null);
  const error = failure?.future === future ? failure.message : null;
  const loading = data?.future !== future && !error;

  useEffect(() => {
    // The previous list stays on screen while the toggle reloads; a late answer for the other
    // toggle position is dropped rather than shown under the wrong switch.
    let current = true;
    fetchReferenceLetterLeavers(future)
      .then((d) => {
        if (!current) return;
        setData(d);
        setFailure(null);
      })
      .catch((err: Error) => current && setFailure({ future, message: err.message }));
    return () => {
      current = false;
    };
  }, [future, refreshKey]);

  // The chips count what the search leaves, so a chip's number is always the rows it shows.
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = data?.leavers ?? [];
    return q ? all.filter((l) => `${l.name ?? ''} ${l.supervisorName ?? ''} ${l.position ?? ''}`.toLowerCase().includes(q)) : all;
  }, [data, search]);
  const statuses = useMemo(() => searched.map(statusOf), [searched]);
  const shown = useMemo(
    () => searched.filter((_, i) => showsStatus(statusFilter, statuses[i].label)),
    [searched, statuses, statusFilter],
  );
  /** Marks a leaver's letter Done by hand or takes it back, and shows the answer on the row. */
  const toggleDone = (l: ReferenceLetterLeaver) => async (done: boolean) => {
    const mark = await markLetterDone(l.request ? { recordId: l.request.id } : { personioId: l.id }, done);
    void logAnalyticsEvent('reference_letters_marked_done', { surface: 'leavers', done });
    // Two contracts of one person can show the same request, and so share its mark.
    const sameLetter = (x: ReferenceLetterLeaver) => (l.request ? x.request?.id === l.request.id : x.id === l.id && !x.request);
    setData((d) => (d ? { ...d, leavers: d.leavers.map((x) => (sameLetter(x) ? { ...x, done: mark } : x)) } : d));
  };
  /** Opens a leaver from the row or its button; the button's click must not also reach the row. */
  const open = (l: ReferenceLetterLeaver) => (e?: MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    onSelect(l);
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusFilter statuses={statuses} selected={statusFilter} onChange={setStatusFilter} />
        <div className="flex items-center gap-4">
          <Switch label="Show future leavers" checked={future} onCheckedChange={setFuture} />
          <div className="w-60">
            <Input className="rounded" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>
      <p className="text-xs text-zinc-600">
        {data ? `Leaving ${formatDate(data.window.from)} – ${future ? 'later' : formatDate(data.window.to)}` : 'Leavers'}
        {loading && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" aria-label="Loading" />}
      </p>

      {error && <Banner variant="alert" description={error} />}
      {/* A failed read hides the rows only when they belong to the other switch position; a failed
          reload of this one (after a letter was sent) keeps them, under the error. */}
      {data && (!error || data.future === future) && (
        <div className="overflow-x-auto rounded border border-solid border-pearl bg-white">
          {/* Fixed layout: the status and action columns keep their width whatever the names hold.
              The minimum width leaves the names room; below it the wrapper scrolls sideways. */}
          <table className="w-full min-w-[56rem] table-fixed text-left text-sm">
            <thead className="text-xs text-zinc-600 [&_th]:font-normal">
              <tr>
                <th className="px-3 py-2">Leaver</th>
                <th className="px-3 py-2">Supervisor</th>
                <th className="w-32 px-3 py-2">Termination date</th>
                <th className="w-60 px-3 py-2">Status</th>
                <th className="w-48 px-3 py-2"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            {shown.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={COLUMNS} className="px-3 py-6 text-center text-zinc-600">No leavers match.</td>
                </tr>
              </tbody>
            )}
            {groupByMonth(shown).map((group) => (
              <tbody key={group.key}>
                <tr className="border-t border-solid border-pearl bg-zinc-50">
                  <th colSpan={COLUMNS} scope="rowgroup" className="px-3 py-1.5 text-left text-xs font-normal text-zinc-600">
                    {group.label} · {group.leavers.length}
                  </th>
                </tr>
                {group.leavers.map((l) => (
                  <tr key={l.id} className="cursor-pointer border-t border-solid border-pearl hover:bg-zinc-50" onClick={open(l)}>
                    <td className="px-3 py-2">
                      <Button type="button" variant="action" size="md" className="text-left before:hidden after:hidden" onClick={open(l)}>
                        {l.name || 'Unnamed'}
                      </Button>
                      {l.position && <p className="truncate text-xs text-zinc-600" title={l.position}>{l.position}</p>}
                    </td>
                    <td className="px-3 py-2 text-zinc-900">{l.supervisorName || '–'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-zinc-900">{formatDate(l.terminationDate)}</td>
                    <td className="px-3 py-2" title={l.done ? markedDoneText(l.done) : undefined}><LetterStatusBadge status={statusOf(l)} /></td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <div className="flex items-start justify-end gap-2">
                        {/* A letter marked Done needs nothing more: no Remind, no Draft. */}
                        <div>
                          {l.done ? null : !isComplete(l) ? (
                            // Keyed by the switch: a reload starts the control afresh, so no stale error stays.
                            <RemindControl key={`${future}-${l.id}`} leaver={l} />
                          ) : isReady(l) ? (
                            // Draft while there is drafting to do — the "Ready to draft" rule.
                            <Button type="button" variant="primary" size="sm" className={ACTION_BUTTON} onClick={open(l)}>Draft</Button>
                          ) : null}
                        </div>
                        <DoneMenu done={Boolean(l.done)} onToggle={toggleDone(l)} label={l.name || 'this leaver'} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
}
